// src/components/settings/TeamMemberModal.js
// Full HR profile suite for Team Members (non-login employees).
// Writes to organizations/{orgId}/fieldWorkers — the single source of truth
// for all employee HR data, whether or not the person has app access.
// Activation creates a linked users doc via activateFieldWorker CF.
// Termination/resignation preserves the record per 7-year legal requirement.

import React, { useState, useEffect } from 'react';
import {
    X, Trash2, UploadCloud, AlertTriangle, FileText,
    UserCheck, Check, Loader2, ChevronDown, ChevronUp,
    User, Briefcase, Calendar, BookOpen, FolderOpen, Phone,
    ShieldAlert
} from 'lucide-react';
import { formatPhoneNumber, formatLocalDate } from '../../utils/formatters'; // localstamp-5a8e1c07 — LOCAL calendar day for the default termination date (was UTC via toISOString)
import { db, functions } from '../../firebase/config';
import {
    collection, query, where, onSnapshot, addDoc, deleteDoc,
    doc, getDoc, updateDoc, addDoc as addDocAlias, serverTimestamp, setDoc, deleteField
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FIELDWORKER_PROFILE_FIELDS, resolveProfileRead } from '../../utils/profilePrivacy'; // profcut-4b9d2e63
import { countries, states } from '../../utils/data';
import { parseISO } from 'date-fns';
import useStorage from '../../hooks/useStorage';
import { can, PERMISSIONS, ACTIVATION_ROLES, roleLabel } from '../../utils/permissions';

// profcut-4b9d2e63 — birth/start dates on Team Members are stored as 'YYYY-MM-DD' strings (this
// modal has always saved them that way) but older loaders only understood Timestamps, so a string
// date showed blank and the next save erased it (production: all 4 Alberni records). Accept both.
const toDateInputValue = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : '';
    return v?.toDate?.()?.toISOString().split('T')[0] || '';
};

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVATE MODAL — promotes a Team Member to a full app user
// Preserved exactly from FieldWorkerModal — same logic, same UX
// ─────────────────────────────────────────────────────────────────────────────
export const ActivateTeamMemberModal = ({ teamMember, onClose, currentUserProfile }) => {
    const [role, setRole] = useState(ACTIVATION_ROLES[0]); // Field Technician
    const [isActivating, setIsActivating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const ROLES = ACTIVATION_ROLES; // leadhand-9cb21d86 — = functions/utils/roleGate.js ACTIVATION_ROLES

    const handleActivate = async () => {
        if (!role) { setError('Please select a role.'); return; }
        if (!teamMember.email) {
            setError('An email address is required to activate app access. Add one before activating.');
            return;
        }
        setIsActivating(true);
        setError('');
        try {
            const activateFieldWorker = httpsCallable(functions, 'activateFieldWorker');
            await activateFieldWorker({ fieldWorkerId: teamMember.id, role });
            setSuccess(true);
            setTimeout(() => onClose(true), 2000);
        } catch (err) {
            console.error('Activation error:', err);
            setError(err.message || 'Activation failed. Please try again.');
            setIsActivating(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Activate as App User</h2>
                        <p className="text-sm text-gray-500 mt-0.5">{teamMember.name}</p>
                    </div>
                    <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600 p-1">
                        <X size={20} />
                    </button>
                </div>

                {success ? (
                    <div className="flex flex-col items-center py-6 gap-3">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <Check size={22} className="text-green-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-800 text-center">
                            Activation successful! An invite email has been sent to {teamMember.email}.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800 space-y-1">
                            <p className="font-semibold">What happens when you activate:</p>
                            <ul className="space-y-1 text-blue-700 text-xs mt-1 list-disc list-inside">
                                <li>A login invite is sent to {teamMember.email || '(no email set)'}</li>
                                <li>They can log into Spatchy with the selected role</li>
                                <li>All HR data, job history and route assignments are preserved</li>
                                <li>They count as one user against your plan limit</li>
                            </ul>
                        </div>

                        {!teamMember.email && (
                            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                                <span>No email on file. Add one in the Main Info tab before activating.</span>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Assign Role</label>
                            <select
                                value={role}
                                onChange={e => setRole(e.target.value)}
                                className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8"
                            >
                                {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                            </select>
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                <AlertTriangle size={14} /> {error}
                            </div>
                        )}

                        <div className="flex gap-2 pt-1">
                            <button onClick={() => onClose(false)}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleActivate} disabled={isActivating || !teamMember.email}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                {isActivating
                                    ? <><Loader2 size={15} className="animate-spin" /> Activating…</>
                                    : <><UserCheck size={15} /> Activate</>
                                }
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TERMINATION MODAL — records termination or resignation
// Preserves the record per legal requirements; never deletes
// ─────────────────────────────────────────────────────────────────────────────
export const TerminationModal = ({ teamMember, onClose, currentUserProfile }) => {
    const [terminationType, setTerminationType] = useState('terminated'); // 'terminated' | 'resigned'
    const [terminationReason, setTerminationReason] = useState('');
    const [terminationDate, setTerminationDate] = useState(formatLocalDate()); // localstamp-5a8e1c07
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const handleConfirm = async () => {
        if (!terminationDate) { setError('Please select a termination date.'); return; }
        if (!terminationReason.trim()) { setError('Please provide a reason.'); return; }

        setIsSaving(true);
        setError('');
        try {
            const orgId = currentUserProfile.organizationId;
            const fwRef = doc(db, `organizations/${orgId}/fieldWorkers`, teamMember.id);
            await updateDoc(fwRef, {
                employmentStatus: terminationType,
                terminatedAt: new Date(terminationDate),
                terminationType,
                updatedAt: serverTimestamp(),
            });
            // profcut-4b9d2e63 — the reason is a personal detail: it goes to the Team Member's
            // private/profile (managers only), never the main record every member can read.
            await setDoc(doc(db, `organizations/${orgId}/fieldWorkers/${teamMember.id}/private`, 'profile'), {
                terminationReason: terminationReason.trim(),
                updatedAt: serverTimestamp(),
            }, { merge: true });

            // If they have a linked users account, disable it via CF
            if (teamMember.activatedUid) {
                try {
                    const terminateEmployee = httpsCallable(functions, 'terminateEmployee');
                    await terminateEmployee({ uid: teamMember.activatedUid, terminationType });
                } catch (cfErr) {
                    console.warn('Could not disable auth account — may already be inactive:', cfErr.message);
                }
            }

            onClose(true);
        } catch (err) {
            console.error('Termination error:', err);
            setError(err.message || 'Failed to record termination. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">End Employment</h2>
                        <p className="text-sm text-gray-500 mt-0.5">{teamMember.name}</p>
                    </div>
                    <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600 p-1">
                        <X size={20} />
                    </button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2">
                    <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
                    <span>The employee record is preserved for 7 years as required by Canadian employment law. This cannot be undone.</span>
                </div>

                {/* Termination type */}
                <div className="grid grid-cols-2 gap-2">
                    {[
                        { value: 'terminated', label: 'Terminated', desc: 'Company-initiated' },
                        { value: 'resigned', label: 'Resigned', desc: 'Employee-initiated' },
                    ].map(opt => (
                        <button key={opt.value} type="button"
                            onClick={() => setTerminationType(opt.value)}
                            className={`flex flex-col items-start p-3 rounded-xl border-2 text-left transition-colors ${
                                terminationType === opt.value
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <span className={`text-sm font-semibold ${terminationType === opt.value ? 'text-red-700' : 'text-gray-700'}`}>
                                {opt.label}
                            </span>
                            <span className="text-xs text-gray-400 mt-0.5">{opt.desc}</span>
                        </button>
                    ))}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {terminationType === 'resigned' ? 'Resignation' : 'Termination'} Date
                    </label>
                    <input type="date" value={terminationDate}
                        onChange={e => setTerminationDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
                    <textarea
                        value={terminationReason}
                        onChange={e => setTerminationReason(e.target.value)}
                        placeholder="Brief description of reason..."
                        rows={3}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                </div>

                {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle size={14} /> {error}
                    </div>
                )}

                <div className="flex gap-2">
                    <button onClick={() => onClose(false)}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button onClick={handleConfirm} disabled={isSaving}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors">
                        {isSaving
                            ? <><Loader2 size={15} className="animate-spin" /> Saving…</>
                            : `Confirm ${terminationType === 'resigned' ? 'Resignation' : 'Termination'}`
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN TEAM MEMBER MODAL
// ─────────────────────────────────────────────────────────────────────────────
const TeamMemberModal = ({ onClose, editingTeamMember, currentUserProfile }) => {
    const tm = editingTeamMember || {};
    const orgId = currentUserProfile?.organizationId;

    // tmdupe-4c7a1e93 — this modal deliberately stays open after a create ("...continue editing"),
    // so a create/edit flag derived from the prop alone is wrong the moment the record exists:
    // every subsequent Save ran addDoc again and minted another record (production: 4 Alberni
    // records in 76 seconds). Hold the id of whatever record this modal owns — arrived as a prop,
    // or just created — and derive everything from that. `isNew` now means "no record yet", which
    // is what all dozen of its uses actually intend.
    const [createdId, setCreatedId] = useState(null);
    const recordId = tm.id || createdId;
    const isNew = !recordId;

    const [activeTab, setActiveTab] = useState('main');
    const [showActivate, setShowActivate] = useState(false);
    const [showTerminate, setShowTerminate] = useState(false);
    const { uploadFile, deleteFile, progress, isUploading } = useStorage();
    const [activeUploadField, setActiveUploadField] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [newTimeOff, setNewTimeOff] = useState({ start: '', end: '', type: 'Vacation', notes: '', file: null });

    const canEditSensitive = can(currentUserProfile, PERMISSIONS.VIEW_SENSITIVE_INFO);
    const canEditSchedule = ['Admin', 'Owner', 'Owner/System Admin', 'System Admin'].includes(currentUserProfile?.role);

    const isTerminated = ['terminated', 'resigned'].includes(tm.employmentStatus);

    const initialFormData = {
        // I. CORE IDENTITY
        name: tm.name || '',
        email: tm.email || '',
        phone: tm.phone || '',
        workerType: tm.workerType || 'Field Technician',
        employmentStatus: tm.employmentStatus || 'active',
        notes: tm.notes || '',
        contactDetails: tm.contactDetails || '',

        // II. PERSONAL
        legalName: tm.legalName || '',
        homeAddress1: tm.homeAddress1 || '',
        homeAddress2: tm.homeAddress2 || '',
        homeCity: tm.homeCity || '',
        homeState: tm.homeState || '',
        homeZip: tm.homeZip || '',
        homeCountry: tm.homeCountry || 'CA',
        birthDate: toDateInputValue(tm.birthDate), // profcut-4b9d2e63
        startDate: toDateInputValue(tm.startDate),

        // III. COMPENSATION
        // r8-comp-private-5d2c9f41 — hourlyRate/salaryWage now live in the fieldWorker's
        // private/comp subdoc (Owner/Admin/Manager only), not on the record `tm` comes from.
        // Seeded to 0 and loaded in the effect below.
        compensationType: tm.compensationType || 'Hourly',
        hourlyRate: 0,
        salaryWage: 0,
        payFrequency: tm.payFrequency || 'Bi-Weekly',
        bonusPlan: tm.bonusPlan || '',
        isExempt: tm.isExempt || false,

        // IV. WORK SCHEDULE
        schedulePreset: tm.schedulePreset || '',
        workDays: tm.workDays || null,
        workDaysPerWeek: tm.workDaysPerWeek || '',
        workHoursPerDay: tm.workHoursPerDay || '',
        shiftTrackingMode: tm.shiftTrackingMode || '',

        // V. TIME OFF
        vacationWeeksAllotted: tm.vacationWeeksAllotted || 2,
        timeOffHistory: tm.timeOffHistory || [],

        // VI. EDUCATION
        undergradDegree: tm.undergradDegree || '',
        mastersDegree: tm.mastersDegree || '',
        doctorateDegree: tm.doctorateDegree || '',

        // VII. DOCUMENTS
        directDepositFormUrl: tm.directDepositFormUrl || '',
        certifications: tm.certifications || [],
        disciplinaryForms: tm.disciplinaryForms || [],

        // VIII. EMERGENCY CONTACT
        emergencyName: tm.emergencyName || '',
        emergencyRelationship: tm.emergencyRelationship || '',
        emergencyPhone1: tm.emergencyPhone1 || '',
        emergencyPhone2: tm.emergencyPhone2 || '',
        emergencyEmail: tm.emergencyEmail || '',
    };

    const [formData, setFormData] = useState(initialFormData);

    const [salaryDisplayValue, setSalaryDisplayValue] = useState(() => {
        const val = parseFloat(initialFormData.salaryWage) || 0;
        return val > 0 ? val.toLocaleString('en-CA') : '';
    });

    // r8-comp-private-5d2c9f41 — wages load from fieldWorkers/{id}/private/comp (Owner/Admin/
    // Manager only). Until they resolve — or if the read is denied — the wage inputs are disabled
    // and omitted from the save, so a form that never saw the real rates can't overwrite them.
    // tmdupe-4c7a1e93 — keyed on the PROP id, not `isNew`. This loader exists to pull the stored
    // wages for a record opened for editing; after an in-session create the form already holds the
    // truth, and re-running it would be a wasted read that could clobber what is being typed.
    const [compState, setCompState] = useState(tm.id ? 'loading' : 'ready'); // 'loading' | 'ready' | 'denied'
    const compEditable = compState === 'ready';

    useEffect(() => {
        if (!orgId || !tm.id) return;
        let cancelled = false;

        (async () => {
            try {
                const snap = await getDoc(doc(db, `organizations/${orgId}/fieldWorkers/${tm.id}/private`, 'comp'));
                if (cancelled) return;
                const c = snap.exists() ? snap.data() : {};
                setFormData(p => ({
                    ...p,
                    hourlyRate: c.hourlyRate ?? 0,
                    salaryWage: c.salaryWage ?? 0,
                }));
                const sal = parseFloat(c.salaryWage) || 0;
                setSalaryDisplayValue(sal > 0 ? sal.toLocaleString('en-CA') : '');
                setCompState('ready');
            } catch (err) {
                if (cancelled) return;
                console.warn('Compensation is not visible to your role:', err?.message);
                setCompState('denied');
            }
        })();

        return () => { cancelled = true; };
    }, [orgId, tm.id]);

    // profcut-4b9d2e63 — personal details live in fieldWorkers/{id}/private/profile (Owner/Admin/
    // Manager only), not on the main Team Member record every org member can read. Load on open; a
    // key on the private record wins over a not-yet-moved main-record copy. Saving is blocked until
    // this resolves, and personal fields are only saved (and their main-record copies removed) when
    // it succeeded.
    // tmdupe-4c7a1e93 — keyed on the PROP id, not `isNew` (see the comp loader above).
    const [profileState, setProfileState] = useState(tm.id ? 'loading' : 'ready'); // 'loading' | 'ready' | 'denied'
    const [terminationReasonShown, setTerminationReasonShown] = useState(tm.terminationReason || '');

    useEffect(() => {
        if (!orgId || !tm.id) return;
        let cancelled = false;

        (async () => {
            try {
                const snap = await getDoc(doc(db, `organizations/${orgId}/fieldWorkers/${tm.id}/private`, 'profile'));
                if (cancelled) return;
                const p = resolveProfileRead(tm, snap.exists() ? snap.data() : {}, FIELDWORKER_PROFILE_FIELDS);
                setFormData(prev => ({
                    ...prev,
                    notes: p.notes || '',
                    contactDetails: p.contactDetails || '',
                    legalName: p.legalName || '',
                    homeAddress1: p.homeAddress1 || '',
                    homeAddress2: p.homeAddress2 || '',
                    homeCity: p.homeCity || '',
                    homeState: p.homeState || '',
                    homeZip: p.homeZip || '',
                    homeCountry: p.homeCountry || 'CA',
                    birthDate: toDateInputValue(p.birthDate),
                    startDate: toDateInputValue(p.startDate),
                    vacationWeeksAllotted: p.vacationWeeksAllotted || 2,
                    timeOffHistory: p.timeOffHistory || [],
                    undergradDegree: p.undergradDegree || '',
                    mastersDegree: p.mastersDegree || '',
                    doctorateDegree: p.doctorateDegree || '',
                    directDepositFormUrl: p.directDepositFormUrl || '',
                    certifications: p.certifications || [],
                    disciplinaryForms: p.disciplinaryForms || [],
                    emergencyName: p.emergencyName || '',
                    emergencyRelationship: p.emergencyRelationship || '',
                    emergencyPhone1: p.emergencyPhone1 || '',
                    emergencyPhone2: p.emergencyPhone2 || '',
                    emergencyEmail: p.emergencyEmail || '',
                }));
                setTerminationReasonShown(p.terminationReason || '');
                setProfileState('ready');
            } catch (err) {
                if (cancelled) return;
                console.warn('Personal details are not visible to your role:', err?.message);
                setProfileState('denied');
            }
        })();

        return () => { cancelled = true; };
    }, [orgId, tm.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Constants ────────────────────────────────────────────────────────────
    const WORKER_TYPES = [
        'Field Technician', 'Office / Admin', 'Delivery Driver',
        'Seasonal Staff', 'Subcontractor', 'Other'
    ];
    const EMPLOYMENT_STATUSES = [
        { value: 'active',   label: 'Active' },
        { value: 'inactive', label: 'Inactive (still employed)' },
        { value: 'on_leave', label: 'On Leave' },
    ];
    const PAY_FREQUENCIES = ['Weekly', 'Bi-Weekly', 'Semi-Monthly', 'Monthly'];
    const SCHEDULE_PRESETS = { '5x8': [5, 8], '4x10': [4, 10], '3x12': [3, 12] };
    const PRESET_WORK_DAYS = {
        '5x8':  ['monday','tuesday','wednesday','thursday','friday'],
        '4x10': ['monday','tuesday','wednesday','thursday'],
        '3x12': ['monday','wednesday','friday'],
    };
    const ALL_DAYS = [
        { key: 'sunday', label: 'Sun' }, { key: 'monday', label: 'Mon' },
        { key: 'tuesday', label: 'Tue' }, { key: 'wednesday', label: 'Wed' },
        { key: 'thursday', label: 'Thu' }, { key: 'friday', label: 'Fri' },
        { key: 'saturday', label: 'Sat' },
    ];

    // ── Computed time-off totals ──────────────────────────────────────────────
    const calculateDaysTaken = (type) =>
        (formData.timeOffHistory || [])
            .filter(e => e.type === type && e.status === 'Approved')
            .reduce((sum, e) => sum + (Number(e.days) || 0), 0);

    const daysAllotted = (formData.vacationWeeksAllotted || 0) * 5;
    const vacationDaysTaken = calculateDaysTaken('Vacation');
    const vacationDaysRemaining = daysAllotted - vacationDaysTaken;
    const sickDaysTaken = calculateDaysTaken('Sick');
    const personalDaysTaken = calculateDaysTaken('Personal');

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let val = type === 'checkbox' ? checked
            : ['hourlyRate', 'salaryWage', 'vacationWeeksAllotted'].includes(name) ? (value === '' ? '' : parseFloat(value))
            : type === 'tel' ? formatPhoneNumber(value)
            : value;
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handleSalaryInput = (e) => {
        const raw = e.target.value.replace(/,/g, '');
        if (raw === '' || raw === '.') { setSalaryDisplayValue(raw); setFormData(p => ({ ...p, salaryWage: 0 })); return; }
        if (isNaN(parseFloat(raw))) return;
        const parts = raw.split('.');
        const formatted = parseInt(parts[0], 10).toLocaleString('en-CA') + (parts.length > 1 ? '.' + parts[1] : '');
        setSalaryDisplayValue(formatted);
        setFormData(p => ({ ...p, salaryWage: parseFloat(raw) }));
    };

    const handleScheduleChange = (field, value) => {
        if (!canEditSchedule) return;
        setFormData(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'schedulePreset' && SCHEDULE_PRESETS[value]) {
                updated.workDaysPerWeek = SCHEDULE_PRESETS[value][0];
                updated.workHoursPerDay = SCHEDULE_PRESETS[value][1];
                if (PRESET_WORK_DAYS[value]) updated.workDays = [...PRESET_WORK_DAYS[value]];
            }
            if (field === 'schedulePreset' && value === '') updated.workDays = null;
            return updated;
        });
    };

    const handleDayToggle = (dayKey) => {
        if (!canEditSchedule) return;
        setFormData(prev => {
            const current = prev.workDays || PRESET_WORK_DAYS[prev.schedulePreset] || [];
            const isSelected = current.includes(dayKey);
            const cap = prev.schedulePreset === 'custom' ? 7 : SCHEDULE_PRESETS[prev.schedulePreset]?.[0] ?? 7;
            if (!isSelected && current.length >= cap) return prev;
            const next = isSelected ? current.filter(d => d !== dayKey) : [...current, dayKey];
            return { ...prev, workDays: next, workDaysPerWeek: next.length };
        });
    };

    // ── File upload ───────────────────────────────────────────────────────────
    const handleFileUpload = async (e, fieldName) => {
        const file = e.target.files[0];
        if (!file || !orgId) return;
        if (isNew) { alert('Please save the team member first before uploading documents.'); return; }

        setActiveUploadField(fieldName);
        setError('');
        // tmdupe-4c7a1e93 — recordId, not tm.id: after an in-session create the prop is still empty
        // and this path would have contained the literal "undefined".
        const storagePath = `organizations/${orgId}/teamMembers/${recordId}/hr_docs/${fieldName}_${Date.now()}_${file.name}`;
        try {
            const downloadURL = await uploadFile(file, storagePath);
            if (fieldName === 'directDepositFormUrl') {
                setFormData(p => ({ ...p, directDepositFormUrl: downloadURL }));
            } else {
                const newDoc = { name: file.name, url: downloadURL, uploadedAt: new Date().toISOString(), type: fieldName, storagePath };
                setFormData(p => ({ ...p, [fieldName]: [...(p[fieldName] || []), newDoc] }));
            }
            setSuccessMessage('Document uploaded. Click Save to confirm.');
        } catch (err) {
            console.error('Upload error:', err);
            setError('File upload failed.');
        } finally {
            setActiveUploadField(null);
        }
    };

    const handleDeleteDocument = async (fileName, fieldName, docPath = null) => {
        if (!window.confirm('Remove this document?')) return;
        if (docPath) { try { await deleteFile(docPath); } catch (e) { console.warn('Storage delete failed:', e); } }
        if (fieldName === 'directDepositFormUrl') {
            setFormData(p => ({ ...p, directDepositFormUrl: '' }));
        } else {
            setFormData(p => ({ ...p, [fieldName]: p[fieldName].filter(d => d.name !== fileName) }));
        }
        setSuccessMessage('Document removed. Click Save to confirm.');
    };

    // ── Time off ──────────────────────────────────────────────────────────────
    const handleAddTimeOff = async () => {
        if (!newTimeOff.start || !newTimeOff.end) { alert('Please select both start and end dates.'); return; }
        if (isNew) { alert('Please save the team member first before logging time off.'); return; }

        let docUrl = '';
        if (newTimeOff.file) {
            try {
                const path = `organizations/${orgId}/teamMembers/${recordId}/timeoff/${newTimeOff.file.name}_${Date.now()}`;
                docUrl = await uploadFile(newTimeOff.file, path);
            } catch (e) { console.error('Upload failed:', e); alert('Failed to upload document.'); return; }
        }

        const start = parseISO(newTimeOff.start);
        const end = parseISO(newTimeOff.end);
        const daysDiff = start <= end ? Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1 : 0;

        // Write calendar event so time off shows on shared calendar
        let calendarEventId = null;
        try {
            const calendarEnd = new Date(end);
            calendarEnd.setHours(23, 59, 59, 0);
            // tmdupe-4c7a1e93 — name off the form and id off recordId. `tm` is empty for a record
            // created in this session, which would have put "undefined — Vacation" on the calendar.
            const displayName = formData.name.trim();
            const calendarRef = await addDoc(collection(db, `organizations/${orgId}/jobs`), {
                eventType: 'Time Off',
                timeOffType: newTimeOff.type,
                title: `${displayName} — ${newTimeOff.type}`,
                clientName: displayName,
                assignedTo: [],
                assignedFieldWorkerIds: [recordId],
                startAt: start,
                endAt: calendarEnd,
                status: 'Scheduled',
                createdAt: serverTimestamp(),
            });
            calendarEventId = calendarRef.id;
        } catch (e) { console.error('Calendar event creation failed:', e); }

        const newEntry = {
            id: Date.now().toString(),
            startDate: newTimeOff.start,
            endDate: newTimeOff.end,
            type: newTimeOff.type,
            notes: newTimeOff.notes,
            status: 'Approved',
            days: daysDiff,
            docUrl,
            docName: newTimeOff.file ? newTimeOff.file.name : '',
            calendarEventId,
        };

        setFormData(p => ({ ...p, timeOffHistory: [newEntry, ...(p.timeOffHistory || [])] }));
        setNewTimeOff({ start: '', end: '', type: 'Vacation', notes: '', file: null });
    };

    const handleDeleteTimeOff = async (id) => {
        if (!window.confirm('Delete this time off entry?')) return;
        const entry = formData.timeOffHistory.find(e => e.id === id);
        if (entry?.calendarEventId && orgId) {
            try { await deleteDoc(doc(db, `organizations/${orgId}/jobs`, entry.calendarEventId)); }
            catch (e) { console.error('Calendar event delete failed:', e); }
        }
        setFormData(p => ({ ...p, timeOffHistory: p.timeOffHistory.filter(e => e.id !== id) }));
        setSuccessMessage('Entry removed. Click Save to confirm.');
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!formData.name.trim()) { setError('Name is required.'); return; }
        if (!orgId) { setError('Organization ID missing. Please refresh.'); return; }
        // profcut-4b9d2e63 — never save personal details before they have loaded.
        if (profileState === 'loading') { setError('Still loading personal details — please try again in a moment.'); return; }

        setIsSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            const dataToSave = {
                name: formData.name.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.trim(),
                workerType: formData.workerType,
                employmentStatus: formData.employmentStatus || 'active',
                status: formData.employmentStatus === 'active' || formData.employmentStatus === 'on_leave' ? 'active' : 'inactive',
                // profcut-4b9d2e63 — notes, legal name, home address, dates, time off, education,
                // documents and emergency contact are NOT written to this main record any more (the
                // rules refuse it); they go to private/profile in saveProfile below.
                compensationType: formData.compensationType,
                // r8-comp-private-5d2c9f41 — hourlyRate/salaryWage are NOT written here any more;
                // they go to the private/comp subdoc below. compensationType stays on the parent
                // (it's not a wage, and getLaborCostRate reads it from the parent doc).
                payFrequency: formData.payFrequency,
                bonusPlan: formData.bonusPlan.trim(),
                isExempt: formData.isExempt,
                schedulePreset: formData.schedulePreset || null,
                workDays: formData.workDays || null,
                workDaysPerWeek: formData.workDaysPerWeek !== '' ? Number(formData.workDaysPerWeek) : null,
                workHoursPerDay: formData.workHoursPerDay !== '' ? Number(formData.workHoursPerDay) : null,
                shiftTrackingMode: formData.shiftTrackingMode || null,
                updatedAt: serverTimestamp(),
            };

            // profcut-4b9d2e63 — the same values this form always saved, now bound for
            // fieldWorkers/{id}/private/profile (Owner/Admin/Manager only).
            const profileToSave = {
                notes: formData.notes.trim(),
                contactDetails: formData.contactDetails.trim(),
                legalName: formData.legalName.trim(),
                homeAddress1: formData.homeAddress1.trim(),
                homeAddress2: formData.homeAddress2.trim(),
                homeCity: formData.homeCity.trim(),
                homeState: formData.homeState,
                homeZip: formData.homeZip.trim(),
                homeCountry: formData.homeCountry,
                birthDate: formData.birthDate || null,
                startDate: formData.startDate || null,
                vacationWeeksAllotted: parseInt(formData.vacationWeeksAllotted) || 0,
                timeOffHistory: formData.timeOffHistory,
                undergradDegree: formData.undergradDegree.trim(),
                mastersDegree: formData.mastersDegree.trim(),
                doctorateDegree: formData.doctorateDegree.trim(),
                directDepositFormUrl: formData.directDepositFormUrl,
                certifications: formData.certifications,
                disciplinaryForms: formData.disciplinaryForms,
                emergencyName: formData.emergencyName.trim(),
                emergencyRelationship: formData.emergencyRelationship.trim(),
                emergencyPhone1: formData.emergencyPhone1.trim(),
                emergencyPhone2: formData.emergencyPhone2.trim(),
                emergencyEmail: formData.emergencyEmail.trim(),
                // Written by TerminationModal, not this form — carried so removing the legacy
                // main-record copy below can never lose it.
                ...(terminationReasonShown ? { terminationReason: terminationReasonShown } : {}),
                updatedAt: serverTimestamp(),
            };
            const saveProfile = async (fieldWorkerId) => {
                if (profileState !== 'ready') return;
                await setDoc(
                    doc(db, `organizations/${orgId}/fieldWorkers/${fieldWorkerId}/private`, 'profile'),
                    profileToSave,
                    { merge: true }
                );
            };
            // Once the private copy is saved, remove any legacy copies still on the main record
            // (the rules allow removing these keys, never adding or changing them).
            const legacyProfileDeletes = profileState === 'ready'
                ? Object.fromEntries(FIELDWORKER_PROFILE_FIELDS
                    .filter(f => Object.prototype.hasOwnProperty.call(tm, f))
                    .map(f => [f, deleteField()]))
                : {};

            // r8-comp-private-5d2c9f41 — wages land in the private subdoc, and only when this form
            // actually loaded them (compEditable). Written after the parent so a new record has an
            // id to hang it off.
            const saveComp = async (fieldWorkerId) => {
                if (!compEditable) return;
                await setDoc(
                    doc(db, `organizations/${orgId}/fieldWorkers/${fieldWorkerId}/private`, 'comp'),
                    {
                        hourlyRate: parseFloat(formData.hourlyRate) || 0,
                        salaryWage: parseFloat(formData.salaryWage) || 0,
                        updatedAt: serverTimestamp(),
                    },
                    { merge: true }
                );
            };

            if (!recordId) {
                const created = await addDoc(collection(db, `organizations/${orgId}/fieldWorkers`), {
                    ...dataToSave,
                    createdAt: serverTimestamp(),
                });
                await saveComp(created.id);
                await saveProfile(created.id);
                // tmdupe-4c7a1e93 — remember what we just made. Without this the modal stays in
                // "create" mode while it invites the user to keep editing, and the next Save mints
                // another record.
                setCreatedId(created.id);
                setSuccessMessage('Team member created! You can close this window or continue editing.');
            } else {
                // Private copy first, so a failure there never leaves the main-record copy deleted.
                await saveProfile(recordId);
                await updateDoc(doc(db, `organizations/${orgId}/fieldWorkers`, recordId), { ...dataToSave, ...legacyProfileDeletes });
                await saveComp(recordId);
                setSuccessMessage('Saved successfully!');
            }
        } catch (err) {
            console.error('Save error:', err);
            setError(err.message || 'Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // TAB RENDERERS
    // ─────────────────────────────────────────────────────────────────────────

    const renderMainInfo = () => (
        <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name (for payroll)</label>
                    <input type="text" name="legalName" value={formData.legalName} onChange={handleChange}
                        placeholder="John Michael Doe"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                        <span className="ml-1 text-xs font-normal text-gray-400">(required to activate)</span>
                    </label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                        placeholder="worker@example.com"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        placeholder="(555) 555-5555"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Worker Type</label>
                    <select name="workerType" value={formData.workerType} onChange={handleChange}
                        className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        {WORKER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select name="employmentStatus" value={formData.employmentStatus} onChange={handleChange}
                        className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        {EMPLOYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input type="date" name="startDate" value={formData.startDate} onChange={handleChange}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input type="date" name="birthDate" value={formData.birthDate} onChange={handleChange}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
            </div>

            <div className="pt-3 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-2">Home Address</label>
                <div className="space-y-2">
                    <input type="text" name="homeAddress1" value={formData.homeAddress1} onChange={handleChange}
                        placeholder="Address Line 1"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <input type="text" name="homeAddress2" value={formData.homeAddress2} onChange={handleChange}
                        placeholder="Address Line 2 (optional)"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <div className="grid grid-cols-2 gap-2">
                        <input type="text" name="homeCity" value={formData.homeCity} onChange={handleChange}
                            placeholder="City"
                            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <input type="text" name="homeZip" value={formData.homeZip} onChange={handleChange}
                            placeholder="Postal Code"
                            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <select name="homeCountry" value={formData.homeCountry} onChange={handleChange}
                            className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                            {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                        <select name="homeState" value={formData.homeState} onChange={handleChange}
                            className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                            <option value="">— Province/State —</option>
                            {(states[formData.homeCountry] || []).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                <textarea name="contactDetails" value={formData.contactDetails} onChange={handleChange}
                    rows={3} placeholder="Any internal notes about this team member..."
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>

            {/* Termination banner for terminated/resigned employees */}
            {isTerminated && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-1">
                    <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                        <ShieldAlert size={15} />
                        {tm.employmentStatus === 'resigned' ? 'Resigned' : 'Terminated'} — {tm.terminatedAt?.toDate?.()?.toLocaleDateString('en-CA') || ''}
                    </p>
                    {terminationReasonShown && (
                        <p className="text-xs text-red-600">{terminationReasonShown}</p>
                    )}
                    <p className="text-xs text-red-400">Record preserved per employment law. Read-only mode.</p>
                </div>
            )}
        </div>
    );

    const renderWages = () => (
        <div className="space-y-5">
            <h3 className="text-base font-semibold text-gray-800">Compensation Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pay Frequency</label>
                    <select name="payFrequency" value={formData.payFrequency} onChange={handleChange}
                        className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        {PAY_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee Type</label>
                    <select name="compensationType" value={formData.compensationType} onChange={handleChange}
                        className="w-full appearance-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8">
                        <option value="Hourly">Hourly</option>
                        <option value="Salary">Salary</option>
                    </select>
                </div>
            </div>

            {/* r8-comp-private-5d2c9f41 — wages load from the private comp subdoc; disabled and
                excluded from the save until they do (or if this role can't read them). */}
            {compState === 'denied' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                    <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Wage details are visible to Owners, Admins and Managers only. Everything else on this profile can still be edited and saved.</span>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {formData.compensationType === 'Hourly' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (CAD)</label>
                        <input type="number" name="hourlyRate" value={compEditable ? formData.hourlyRate : ''} onChange={handleChange}
                            disabled={!compEditable}
                            placeholder={compState === 'loading' ? 'Loading…' : compState === 'denied' ? 'Hidden' : 'e.g. 25.00'}
                            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400" />
                    </div>
                )}
                {formData.compensationType === 'Salary' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Annual Salary (CAD)</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input type="text" inputMode="numeric" value={compEditable ? salaryDisplayValue : ''}
                                onChange={handleSalaryInput} disabled={!compEditable}
                                placeholder={compState === 'loading' ? 'Loading…' : compState === 'denied' ? 'Hidden' : 'e.g. 60,000'}
                                className="w-full pl-7 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400" />
                        </div>
                    </div>
                )}
                <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" name="isExempt" checked={formData.isExempt} onChange={handleChange}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm text-gray-700">Exempt from Overtime</span>
                    </label>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bonus / Commission Plan</label>
                <textarea name="bonusPlan" value={formData.bonusPlan} onChange={handleChange}
                    rows={2} placeholder="Describe any bonus or commission structure..."
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>

            {/* Work Schedule */}
            <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-gray-800">Work Schedule & Shift Tracking</h3>
                    {!canEditSchedule && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">View only</span>
                    )}
                </div>
                <p className="text-xs text-gray-400 mb-4">
                    Leave on <span className="font-medium text-gray-500">Org default</span> unless this employee works a different schedule.
                    {!canEditSchedule && <span className="text-amber-600 font-medium"> Only Admins and Owners can edit.</span>}
                </p>

                <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Schedule</label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {[
                            { value: '', label: 'Org default', sub: 'recommended' },
                            { value: '5x8', label: '5 × 8h', sub: '40h week' },
                            { value: '4x10', label: '4 × 10h', sub: '40h week' },
                            { value: '3x12', label: '3 × 12h', sub: '36h week' },
                            { value: 'custom', label: 'Custom', sub: 'set below' },
                        ].map(opt => {
                            const active = formData.schedulePreset === opt.value;
                            return (
                                <button key={opt.value} type="button"
                                    onClick={() => handleScheduleChange('schedulePreset', opt.value)}
                                    disabled={!canEditSchedule}
                                    className={`flex flex-col items-center justify-center rounded-xl border-2 py-2 px-1 text-xs font-semibold transition-colors
                                        ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'}
                                        ${!canEditSchedule ? 'opacity-60 cursor-not-allowed' : ''}`}>
                                    {opt.label}
                                    <span className={`font-normal mt-0.5 ${active ? 'text-blue-400' : 'text-gray-400'}`}>{opt.sub}</span>
                                </button>
                            );
                        })}
                    </div>

                    {formData.schedulePreset === 'custom' && (
                        <div className="mt-3">
                            <label className="block text-xs text-gray-500 mb-1">Hours per day</label>
                            <input type="number" min="1" max="24" step="0.5"
                                value={formData.workHoursPerDay}
                                onChange={e => handleScheduleChange('workHoursPerDay', e.target.value)}
                                disabled={!canEditSchedule}
                                className="w-32 p-2 border rounded-xl text-sm disabled:bg-gray-50" />
                        </div>
                    )}

                    {formData.schedulePreset !== '' && (
                        <div className="mt-4">
                            <label className="block text-xs font-medium text-gray-600 mb-2">Which days does this employee work?</label>
                            {(() => {
                                const activeDays = formData.workDays || PRESET_WORK_DAYS[formData.schedulePreset] || [];
                                const cap = formData.schedulePreset === 'custom' ? 7 : SCHEDULE_PRESETS[formData.schedulePreset]?.[0] ?? 7;
                                const atCap = activeDays.length >= cap;
                                return (
                                    <div className="flex gap-1.5 flex-wrap">
                                        {ALL_DAYS.map(day => {
                                            const isChecked = activeDays.includes(day.key);
                                            const isDisabled = !canEditSchedule || (atCap && !isChecked);
                                            return (
                                                <button key={day.key} type="button"
                                                    onClick={() => handleDayToggle(day.key)}
                                                    disabled={isDisabled}
                                                    className={`w-10 h-10 rounded-xl text-xs font-semibold border-2 transition-colors
                                                        ${isChecked ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-500'}
                                                        ${isDisabled && !isChecked ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                                    {day.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shift Tracking Mode</label>
                    <div className="space-y-1.5">
                        {[
                            { value: '', label: 'Use org default', desc: 'Follows the setting in Payroll Settings.' },
                            { value: 'strict', label: 'Strict — must punch in/out', desc: 'Missing shifts block timesheet approval.' },
                            { value: 'estimate', label: 'Estimate — propose from jobs', desc: 'Missing shifts estimated from job time.' },
                            { value: 'off', label: 'Off — use scheduled hours', desc: 'No shift punches required.' },
                        ].map(opt => {
                            const active = formData.shiftTrackingMode === opt.value;
                            return (
                                <label key={opt.value}
                                    className={`flex items-start gap-3 p-2.5 rounded-xl border-2 transition-colors
                                        ${active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-200'}
                                        ${!canEditSchedule ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input type="radio" name="tmShiftTrackingMode" value={opt.value}
                                        checked={active}
                                        onChange={() => handleScheduleChange('shiftTrackingMode', opt.value)}
                                        disabled={!canEditSchedule}
                                        className="mt-0.5 accent-blue-600" />
                                    <div>
                                        <p className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-gray-700'}`}>{opt.label}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Direct Deposit */}
            <div className="pt-4 border-t">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText size={16} className="text-gray-500" /> Direct Deposit Form
                </h3>
                {formData.directDepositFormUrl ? (
                    <div className="flex justify-between items-center bg-green-50 p-3 rounded-xl border border-green-200">
                        <a href={formData.directDepositFormUrl} target="_blank" rel="noopener noreferrer"
                            className="text-green-700 hover:underline text-sm flex items-center gap-1.5">
                            <FileText size={14} /> Direct Deposit Form (Uploaded)
                        </a>
                        <button onClick={() => handleDeleteDocument('Direct Deposit Form', 'directDepositFormUrl')}
                            className="text-red-500 hover:text-red-700 ml-2">
                            <Trash2 size={15} />
                        </button>
                    </div>
                ) : isNew ? (
                    <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 text-sm text-amber-700 flex items-center gap-2">
                        <AlertTriangle size={15} /> Save team member first to upload forms.
                    </div>
                ) : (
                    isUploading && activeUploadField === 'directDepositFormUrl' ? (
                        <div className="w-full bg-gray-100 rounded-xl p-4 border">
                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                            </div>
                            <p className="text-xs text-center text-gray-500">Uploading...</p>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-gray-50 transition-colors relative cursor-pointer">
                            <input type="file" onChange={e => handleFileUpload(e, 'directDepositFormUrl')}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                            <UploadCloud className="mx-auto h-6 w-6 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 font-medium">Click to upload form</p>
                        </div>
                    )
                )}
            </div>
        </div>
    );

    const renderTimeOff = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Vacation Weeks Allotted</label>
                    <input type="number" name="vacationWeeksAllotted" value={formData.vacationWeeksAllotted}
                        onChange={handleChange}
                        className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Stats</label>
                    <div className="text-sm mt-2 space-y-1">
                        <p>Total Days: <span className="font-bold">{daysAllotted}</span></p>
                        <p>Remaining: <span className={`font-bold ${vacationDaysRemaining < 0 ? 'text-red-600' : 'text-green-600'}`}>{vacationDaysRemaining}</span></p>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-base font-bold mb-3">Log Time Off</h3>
                {isNew ? (
                    <div className="bg-amber-50 p-3 rounded-xl text-sm text-amber-800 flex items-center gap-2">
                        <AlertTriangle size={15} className="flex-shrink-0" />
                        Save the team member first before logging time off.
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-2 items-end bg-blue-50 p-3 rounded-xl border border-blue-100">
                        <div className="col-span-3">
                            <label className="text-xs font-semibold">Start</label>
                            <input type="date" value={newTimeOff.start}
                                onChange={e => setNewTimeOff(p => ({ ...p, start: e.target.value }))}
                                className="w-full p-1.5 border rounded-lg text-sm mt-0.5" />
                        </div>
                        <div className="col-span-3">
                            <label className="text-xs font-semibold">End</label>
                            <input type="date" value={newTimeOff.end}
                                onChange={e => setNewTimeOff(p => ({ ...p, end: e.target.value }))}
                                className="w-full p-1.5 border rounded-lg text-sm mt-0.5" />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs font-semibold">Type</label>
                            <select value={newTimeOff.type}
                                onChange={e => setNewTimeOff(p => ({ ...p, type: e.target.value }))}
                                className="w-full appearance-none border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-7 mt-0.5">
                                <option>Vacation</option>
                                <option>Sick</option>
                                <option>Personal</option>
                            </select>
                        </div>
                        <div className="col-span-3">
                            <label className="text-xs font-semibold">Upload (Optional)</label>
                            <input type="file"
                                onChange={e => setNewTimeOff(p => ({ ...p, file: e.target.files[0] }))}
                                className="w-full text-xs border p-1 rounded-lg bg-white mt-0.5" />
                        </div>
                        <div className="col-span-1">
                            <button onClick={handleAddTimeOff} disabled={isUploading}
                                className="w-full py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                                Add
                            </button>
                        </div>
                        <div className="col-span-12">
                            <input type="text" value={newTimeOff.notes}
                                onChange={e => setNewTimeOff(p => ({ ...p, notes: e.target.value }))}
                                placeholder="Notes (optional)"
                                className="w-full p-1.5 border rounded-lg text-sm" />
                        </div>
                    </div>
                )}

                <div className="mt-4 border rounded-xl overflow-hidden">
                    <h4 className="px-3 py-2 bg-gray-100 font-semibold text-sm">Time Off History</h4>
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Dates</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Days</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Notes</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Doc</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {(formData.timeOffHistory || []).map(entry => (
                                <tr key={entry.id}>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs">{entry.startDate} → {entry.endDate}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            entry.type === 'Vacation' ? 'bg-green-100 text-green-800' :
                                            entry.type === 'Sick' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-700'
                                        }`}>{entry.type}</span>
                                    </td>
                                    <td className="px-3 py-2 text-center text-xs">{entry.days}</td>
                                    <td className="px-3 py-2 max-w-[120px] truncate text-xs text-gray-500">{entry.notes || '—'}</td>
                                    <td className="px-3 py-2">
                                        {entry.docUrl && (
                                            <a href={entry.docUrl} target="_blank" rel="noreferrer"
                                                className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                                                <FileText size={12} /> Doc
                                            </a>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => handleDeleteTimeOff(entry.id)}
                                            className="text-red-500 hover:bg-red-50 p-1 rounded">
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {(formData.timeOffHistory || []).length === 0 && (
                                <tr><td colSpan={6} className="text-center py-6 text-sm text-gray-400">No time off recorded.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="bg-green-50 p-2 rounded-xl border border-green-100">Vacation Used: <strong>{vacationDaysTaken}</strong></div>
                    <div className="bg-yellow-50 p-2 rounded-xl border border-yellow-100">Sick Used: <strong>{sickDaysTaken}</strong></div>
                    <div className="bg-gray-50 p-2 rounded-xl border border-gray-100">Personal Used: <strong>{personalDaysTaken}</strong></div>
                </div>
            </div>
        </div>
    );

    const renderEducation = () => (
        <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Educational History</h3>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Undergraduate Degree</label>
                <input type="text" name="undergradDegree" value={formData.undergradDegree} onChange={handleChange}
                    placeholder="e.g. B.Sc. Computer Science"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Masters Degree</label>
                <input type="text" name="mastersDegree" value={formData.mastersDegree} onChange={handleChange}
                    placeholder="e.g. MBA or M.Eng"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doctorate / Professional Degree</label>
                <input type="text" name="doctorateDegree" value={formData.doctorateDegree} onChange={handleChange}
                    placeholder="e.g. Ph.D. or J.D."
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
        </div>
    );

    const DocumentUploadSection = ({ title, fieldName, docs, icon: Icon }) => (
        <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2"><Icon size={16} className="text-gray-500" /> {title}</h3>
            {(docs || []).length > 0 && (
                <div className="space-y-2 mb-3">
                    {docs.map(d => (
                        <div key={d.name} className="flex justify-between items-center bg-gray-50 p-2.5 rounded-xl border border-gray-200">
                            <a href={d.url} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-sm flex items-center gap-1.5 truncate">
                                <FileText size={13} /> {d.name}
                            </a>
                            <button onClick={() => handleDeleteDocument(d.name, fieldName, d.storagePath)}
                                className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {isNew ? (
                <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 text-sm text-amber-700 flex items-center gap-2">
                    <AlertTriangle size={14} /> Save team member first to upload documents.
                </div>
            ) : (
                isUploading && activeUploadField === fieldName ? (
                    <div className="w-full bg-gray-100 rounded-xl p-4 border">
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                            <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                        </div>
                        <p className="text-xs text-center text-gray-500">Uploading...</p>
                    </div>
                ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-gray-50 transition-colors relative cursor-pointer">
                        <input type="file" onChange={e => handleFileUpload(e, fieldName)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                        <UploadCloud className="mx-auto h-5 w-5 text-gray-400 mb-1" />
                        <p className="text-sm text-gray-500">Click to upload</p>
                    </div>
                )
            )}
        </div>
    );

    const renderDocuments = () => (
        <div className="space-y-2">
            <DocumentUploadSection title="Certifications & Training" fieldName="certifications" docs={formData.certifications} icon={FileText} />
            <hr />
            <DocumentUploadSection title="Disciplinary / HR Forms" fieldName="disciplinaryForms" docs={formData.disciplinaryForms} icon={AlertTriangle} />
        </div>
    );

    const renderEmergency = () => (
        <div className="space-y-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Emergency Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input type="text" value={formData.emergencyName}
                        onChange={e => setFormData(p => ({ ...p, emergencyName: e.target.value }))}
                        placeholder="e.g. Jane Smith"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <input type="text" value={formData.emergencyRelationship}
                        onChange={e => setFormData(p => ({ ...p, emergencyRelationship: e.target.value }))}
                        placeholder="e.g. Spouse, Parent, Sibling"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Phone</label>
                    <input type="tel" name="emergencyPhone1" value={formData.emergencyPhone1} onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Phone</label>
                    <input type="tel" name="emergencyPhone2" value={formData.emergencyPhone2} onChange={handleChange}
                        placeholder="(555) 987-6543"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" value={formData.emergencyEmail}
                        onChange={e => setFormData(p => ({ ...p, emergencyEmail: e.target.value }))}
                        placeholder="e.g. jane.smith@email.com"
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
            </div>
        </div>
    );

    const TABS = [
        { id: 'main',      label: 'Main Info',         icon: User      },
        { id: 'wages',     label: 'Wages & Pay',        icon: Briefcase, sensitive: true },
        { id: 'timeOff',   label: 'Time Off',           icon: Calendar  },
        { id: 'education', label: 'Education',          icon: BookOpen  },
        { id: 'documents', label: 'Documents',          icon: FolderOpen },
        { id: 'emergency', label: 'Emergency',          icon: Phone     },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'main':      return renderMainInfo();
            case 'wages':     return canEditSensitive ? renderWages() : <p className="text-sm text-gray-500 mt-4">You don't have permission to view compensation details.</p>;
            case 'timeOff':   return renderTimeOff();
            case 'education': return renderEducation();
            case 'documents': return renderDocuments();
            case 'emergency': return renderEmergency();
            default:          return renderMainInfo();
        }
    };

    const employmentStatusBadge = {
        active:     'bg-green-100 text-green-700',
        inactive:   'bg-gray-100 text-gray-600',
        on_leave:   'bg-blue-100 text-blue-700',
        terminated: 'bg-red-100 text-red-700',
        resigned:   'bg-orange-100 text-orange-700',
    }[tm.employmentStatus || 'active'];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
                <div className="bg-white w-full sm:rounded-2xl sm:max-w-4xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">

                    {/* Header */}
                    <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b flex-shrink-0">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {isNew ? 'Add Team Member' : formData.name}
                            </h2>
                            {!isNew && (
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-sm text-gray-500">{formData.workerType}</span>
                                    {tm.employmentStatus && (
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${employmentStatusBadge}`}>
                                            {tm.employmentStatus === 'on_leave' ? 'On Leave'
                                                : tm.employmentStatus.charAt(0).toUpperCase() + tm.employmentStatus.slice(1)}
                                        </span>
                                    )}
                                    {tm.status === 'activated' && (
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                            App Access
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600 p-1 mt-0.5">
                            <X size={22} />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-0.5 px-4 pt-3 pb-0 border-b overflow-x-auto flex-shrink-0 scrollbar-hide">
                        {TABS.filter(t => !t.sensitive || canEditSensitive).map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.id} type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors -mb-px ${
                                        activeTab === tab.id
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}>
                                    <Icon size={13} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Feedback */}
                    {(error || successMessage) && (
                        <div className={`mx-6 mt-3 px-3 py-2 rounded-xl text-sm flex items-center gap-2 ${
                            error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
                        }`}>
                            {error ? <AlertTriangle size={14} /> : <Check size={14} />}
                            {error || successMessage}
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-5">
                        {renderTabContent()}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t bg-gray-50 sm:rounded-b-2xl flex items-center justify-between gap-2 flex-shrink-0">
                        {/* Left: Activate + Terminate */}
                        <div className="flex items-center gap-2">
                            {!isNew && !isTerminated && tm.status !== 'activated' && (
                                <button onClick={() => setShowActivate(true)}
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors">
                                    <UserCheck size={14} /> Activate
                                </button>
                            )}
                            {!isNew && !isTerminated && can(currentUserProfile, PERMISSIONS.MANAGE_JOBS) && (
                                <button onClick={() => setShowTerminate(true)}
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                                    <ShieldAlert size={14} /> End Employment
                                </button>
                            )}
                        </div>

                        {/* Right: Cancel + Save */}
                        <div className="flex gap-2">
                            <button onClick={() => onClose(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                            {!isTerminated && (
                                <button onClick={handleSave} disabled={isSaving || isUploading}
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                    {isSaving
                                        ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                                        : <><Check size={14} /> {isNew ? 'Create' : 'Save'}</>
                                    }
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showActivate && (
                <ActivateTeamMemberModal
                    // tmdupe-4c7a1e93 — id last: formData carries no id, and tm has none for a
                    // record created in this session. activateFieldWorker needs a real one.
                    teamMember={{ ...tm, ...formData, id: recordId }}
                    currentUserProfile={currentUserProfile}
                    onClose={(didActivate) => {
                        setShowActivate(false);
                        if (didActivate) onClose(true);
                    }}
                />
            )}

            {showTerminate && (
                <TerminationModal
                    // tmdupe-4c7a1e93 — TerminationModal writes to teamMember.id and shows
                    // teamMember.name; both are empty on a record created in this session.
                    // activatedUid stays absent, which is correct for a never-activated record.
                    teamMember={{ ...tm, name: formData.name.trim(), id: recordId }}
                    currentUserProfile={currentUserProfile}
                    onClose={(didTerminate) => {
                        setShowTerminate(false);
                        if (didTerminate) onClose(true);
                    }}
                />
            )}
        </>
    );
};

export default TeamMemberModal;